import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';
import { usePurchaseVendorAccountSelector } from 'src/components/Selectors/PurchaseVendorAccountSelector/usePurchaseVendorAccountSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { PosSelect, PosSelectProps } from 'src/core/POS/PosSelect';
import { PurchaseVendorAccountDialog } from 'src/dialogs/PurchaseVendorAccountDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { VendorAccountEditInfo } from 'src/navigations/Routes/Settings/Views/VendorManagement/Vendors/hooks/useAddVendorAccount';
import { ContentId } from 'src/utils/constants/contentId';
import {
  posChangedField,
  posField,
  safeSetPosChangedField,
} from 'src/utils/posFieldUtils';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ApiException,
  Feature,
  PartialPurchaseVendorAccount,
  PurchaseClient,
  PurchaseVendorAccount,
  PurchaseVendorAccountDetails,
} from 'src/WebApiController';

export type PurchaseVendorAccountSelectionInputProps<
  TFormValues extends FieldValues,
> = Omit<PosSelectProps, 'onChange' | 'valueOptionsContent'> & {
  vendorFieldName: Path<TFormValues>;
  fieldName: Path<TFormValues>;
  partialFieldName: Path<TFormValues>;
};
export const PurchaseVendorAccountSelectionInput = <
  TFormValues extends FieldValues,
>({
  disabled,
  vendorFieldName,
  fieldName,
  partialFieldName,
  allowSearchAsInput,
  ...posSelectProps
}: PurchaseVendorAccountSelectionInputProps<TFormValues>) => {
  const { setValue, clearErrors, watch, formState } =
    useFormContext<TFormValues>();
  const { activeAccountWebClientConfig } = useAppContext();
  const { closeDialog, launchDialog, dialogProps } = useBasicDialog();
  const [currentVendorAccount, setCurrentVendorAccount] =
    useState<VendorAccountEditInfo>();
  const { showErrorDialog } = useClientLogContext();
  const [searchText, setSearchText] = useState<string | undefined>(undefined);

  const vendorId = watch(`${vendorFieldName}.value.id` as Path<TFormValues>);
  const vendorAccount = watch(fieldName);
  const partialVendorAccount = watch(partialFieldName);

  const addVendorAccountText = useContent(ContentId.AddAccountAction);
  const additionalOptions = useMemo(
    () => ({
      [ContentId.AddAccountAction]: addVendorAccountText + '...',
    }),
    [addVendorAccountText]
  );
  const optionsAlwaysShow = useMemo(
    () => ({
      [ContentId.AddAccountAction]: true,
    }),
    []
  );
  const {
    query,
    sortedVendorAccounts,
    posSelectProps: hookPosSelectProps,
  } = usePurchaseVendorAccountSelector({
    vendorId: vendorId,
    disabled,
    additionalOptions,
    searchText,
    purchaseVendorAccountEmail: vendorAccount?.value?.email,
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  useEffect(() => {
    // If we don't have a selected account, auto-select the first one in the list
    if (
      vendorId &&
      (!vendorAccount ||
        (partialVendorAccount?.vendorId !== vendorId &&
          vendorAccount?.value?.vendorId !== vendorId))
    ) {
      const accounts = sortedVendorAccounts;
      if (accounts?.length) {
        clearErrors(fieldName);
        safeSetPosChangedField(setValue, fieldName, accounts[0]);
        setValue(
          partialFieldName,
          null as PathValue<TFormValues, Path<TFormValues>>
        );
      } else {
        safeSetPosChangedField(setValue, fieldName, null);
        setValue(
          partialFieldName,
          null as PathValue<TFormValues, Path<TFormValues>>
        );
      }
    }
  }, [
    clearErrors,
    setValue,
    vendorAccount,
    vendorId,
    query.data,
    fieldName,
    sortedVendorAccounts,
    partialVendorAccount,
    partialFieldName,
  ]);

  const onAddNewVendorAccount = useCallback(
    (searchText?: string) => {
      if (vendorId) {
        setCurrentVendorAccount({
          vendorId: vendorId,
          id: '',
        });
      }

      launchDialog();
    },
    [launchDialog, vendorId]
  );

  const onSaveVendorAccount = useCallback(
    async (vendorAccount: PurchaseVendorAccountDetails) => {
      await tryInvokeApi(
        async () => {
          const newId = await new PurchaseClient(
            activeAccountWebClientConfig
          ).mergeVendorAccount(vendorAccount);

          if (newId) {
            query.refetch();

            safeSetPosChangedField(setValue, fieldName, {
              ...vendorAccount,
              id: newId,
            });
            clearErrors(fieldName);

            closeDialog();
            setCurrentVendorAccount(undefined);
          } else {
            // This should never happen
            // Normal errors would have fallen to the error catch
            showErrorDialog(
              'PurchaseClient.mergeVendorAccount',
              {
                message:
                  'Unable to create or save vendor account ' + vendorAccount.id,
                status: 500,
              } as ApiException,
              {
                trackErrorData: vendorAccount,
              }
            );
          }
        },
        (error) => {
          showErrorDialog('PurchaseClient.mergeVendorAccount', error, {
            trackErrorData: vendorAccount,
          });
        }
      );
    },
    [
      activeAccountWebClientConfig,
      clearErrors,
      closeDialog,
      fieldName,
      query,
      setValue,
      showErrorDialog,
    ]
  );

  const onVendorAccountCancel = useCallback(() => {
    closeDialog();
    setCurrentVendorAccount(undefined);
  }, [closeDialog]);

  return (
    <>
      <PosSelect
        {...posSelectProps}
        {...hookPosSelectProps}
        placeholderText={
          partialVendorAccount?.name ?? hookPosSelectProps.placeholderText
        }
        sortMode="none" // Don't sort. Use current sorting
        valueOptionsAlwaysShow={optionsAlwaysShow}
        disabled={disabled}
        hasErrors={Boolean(formState.errors.vendorAccount?.message)}
        value={vendorAccount?.value?.id}
        allowSearchAsInput={allowSearchAsInput && vendorId != null}
        onChange={(value, searchText) => {
          clearErrors(fieldName);
          if (value) {
            if (value === ContentId.AddAccountAction) {
              onAddNewVendorAccount(searchText);
            } else if (value !== vendorAccount?.value?.id) {
              const newAccount = sortedVendorAccounts!.find(
                (v: PurchaseVendorAccount) => {
                  if (allowSearchAsInput) {
                    // If we are allowing search as input, the value can be just the email
                    return (
                      v.id === value ||
                      (v.email || v.name).toLocaleLowerCase() ===
                        value.toLocaleLowerCase()
                    );
                  } else {
                    return v.id === value;
                  }
                }
              );
              if (newAccount) {
                safeSetPosChangedField(setValue, fieldName, newAccount);
              } else {
                safeSetPosChangedField(setValue, fieldName, null);
                setValue(partialFieldName, {
                  vendorId: vendorId,
                  name: value,
                  email: value,
                } as PathValue<TFormValues, Path<TFormValues>>);
              }
            }
          } else {
            setValue(
              partialFieldName,
              null as PathValue<TFormValues, Path<TFormValues>>
            );
          }
        }}
        onSearchInputChange={setSearchText}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        useVirtualWindow={true}
      />
      {vendorId && currentVendorAccount && (
        <PurchaseVendorAccountDialog
          {...dialogProps}
          vendorAccountId={currentVendorAccount.id}
          vendorId={currentVendorAccount.vendorId}
          onClosed={onVendorAccountCancel}
          onSave={onSaveVendorAccount}
        />
      )}
    </>
  );
};
